import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { fetchReportExportOptions } from '../helpers/downloads/reportIndexDownloads'

const allSelection = () => ({ allUsers: true, userUids: [], excludedUserUids: [] })
const emptyPage = () => ({
  ready: false,
  users: [],
  totalUsers: 0,
  page: 0,
  hasMore: false,
  activities: [],
  loading: false,
  loadingMore: false,
  error: ''
})

export default function useReportExportUsers({ open, validDates, activeTab, startDate, endDate }) {
  const [selection, setSelection] = useState(allSelection)
  const [query, setQuery] = useState('')
  const [remote, setRemote] = useState(emptyPage)
  const [reload, setReload] = useState(0)
  const cache = useRef(new Map())
  const generation = useRef(0)
  const controller = useRef(null)
  const busy = useRef(false)
  const displayKey = useRef('')
  const lastQuery = useRef('')
  const errorKind = useRef('refresh')
  const remoteRef = useRef(remote)
  const context = useRef(null)

  useLayoutEffect(() => {
    remoteRef.current = remote
    context.current = { activeTab, startDate, endDate, selection, search: query }
  }, [remote, activeTab, startDate, endDate, selection, query])

  useEffect(() => {
    if (!open) {
      setRemote(emptyPage())
      return
    }
    setSelection(allSelection())
    setQuery('')
    setRemote(emptyPage())
    cache.current.clear()
  }, [open, activeTab, startDate, endDate])

  const requestPage = useCallback(async (page, append, version, preserve = false) => {
    busy.current = true
    const requestController = new AbortController()
    controller.current = requestController
    setRemote((current) => ({ ...current, loading: !append, loadingMore: append, error: '' }))
    try {
      const data = await fetchReportExportOptions(
        { ...context.current, page },
        requestController.signal
      )
      if (version !== generation.current || requestController.signal.aborted) return
      const received = data.users || []
      received.forEach((user) => cache.current.set(user.user_uid, user))
      setRemote((current) => ({
        ready: true,
        users: preserve
          ? current.users
          : append
            ? [
                ...new Map(
                  [...current.users, ...received].map((user) => [user.user_uid, user])
                ).values()
              ]
            : received,
        totalUsers: data.total_users ?? received.length,
        page: preserve ? current.page : data.pagination?.current_page || page,
        hasMore: preserve ? current.hasMore : Boolean(data.pagination?.has_more),
        activities: data.activities || [],
        loading: false,
        loadingMore: false,
        error: ''
      }))
    } catch (error) {
      if (version === generation.current && !requestController.signal.aborted) {
        errorKind.current = append ? 'more' : 'refresh'
        setRemote((current) => ({
          ...current,
          loading: false,
          loadingMore: false,
          error: error.message
        }))
      }
    } finally {
      if (version === generation.current) busy.current = false
    }
  }, [])

  useEffect(() => {
    const version = ++generation.current
    controller.current?.abort()
    busy.current = false
    if (!open) return undefined
    if (!validDates) {
      setRemote((current) => ({ ...current, loading: false, loadingMore: false }))
      return undefined
    }
    const key = JSON.stringify([activeTab, startDate, endDate, query])
    const preserve = key === displayKey.current && remoteRef.current.page > 0
    displayKey.current = key
    setRemote((current) => ({
      ...current,
      ...(preserve ? {} : { users: [], page: 0, hasMore: false }),
      loading: true,
      loadingMore: false,
      error: ''
    }))
    const delay = query && query !== lastQuery.current ? 250 : 0
    lastQuery.current = query
    const timer = setTimeout(() => requestPage(1, false, version, preserve), delay)
    return () => {
      clearTimeout(timer)
      controller.current?.abort()
    }
  }, [open, validDates, activeTab, startDate, endDate, query, selection, reload, requestPage])

  const loadMore = useCallback(() => {
    const current = remoteRef.current
    if (busy.current || current.loading || !current.hasMore) return
    requestPage(current.page + 1, true, generation.current)
  }, [requestPage])
  const onScroll = (event) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget
    if (scrollHeight - scrollTop - clientHeight <= 48) loadMore()
  }
  const toggleUser = (uid) =>
    setSelection((current) => {
      const key = current.allUsers ? 'excludedUserUids' : 'userUids'
      const values = current[key]
      return {
        ...current,
        [key]: values.includes(uid) ? values.filter((value) => value !== uid) : [...values, uid]
      }
    })
  const allUsers = selection.allUsers && selection.excludedUserUids.length === 0
  const selectedCount = selection.allUsers
    ? Math.max(0, remote.totalUsers - selection.excludedUserUids.length)
    : selection.userUids.length
  const toggleAll = () =>
    setSelection(
      allUsers ? { allUsers: false, userUids: [], excludedUserUids: [] } : allSelection()
    )
  const isSelected = (uid) =>
    selection.allUsers
      ? !selection.excludedUserUids.includes(uid)
      : selection.userUids.includes(uid)
  const selectedUser =
    selectedCount === 1
      ? [...cache.current.values()].find((user) => isSelected(user.user_uid))
      : null
  return {
    ...remote,
    selection,
    query,
    setQuery,
    allUsers,
    selectedCount,
    selectedUser,
    isSelected,
    toggleUser,
    toggleAll,
    onScroll,
    retry: () => (errorKind.current === 'more' ? loadMore() : setReload((value) => value + 1))
  }
}
